import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useProjectSchemas } from './../global-state/useProjectSchemas';
import { AuthContext } from './../auth/Auth';
import { firebaseHelper } from 'ui';
import { usePermissions } from 'ui';

/**
 * Custom hook that ensures projects are loaded in the Zustand store
 * Can be used in any component that needs access to projects
 */
export const useEnsureProjects = () => {
  const { currentUser } = useContext(AuthContext);
  
  // Add debugging to usePermissions
  const permissionsResult = usePermissions(currentUser?.uid, firebaseHelper.db);
  const { permissions, loading: permissionsLoading } = permissionsResult;

  const selector = useCallback((state) => ({
    projects: state.projects,
    ownedProjects: state.ownedProjects,
    sharedProjects: state.sharedProjects,
    isProjectsLoading: state.isProjectsLoading,
    projectsLoaded: state.projectsLoaded,
    getProjectSchemas: state.getProjectSchemas,
  }), []);
  
  const zustandResult = useProjectSchemas(selector);

  const { projects, ownedProjects, sharedProjects, isProjectsLoading, projectsLoaded, getProjectSchemas } = zustandResult;

  // SIMPLE FIX: Use string comparison instead of array comparison
  const permissionsKey = useMemo(() => {
    if (!permissions) return '';
    const readIds = (permissions.projectRead || []).sort().join(',');
    const writeIds = (permissions.projectWrite || []).sort().join(',');
    return `${readIds}|${writeIds}`;
  }, [permissions?.projectRead, permissions?.projectWrite]);

  // Calculate sharedProjectIds only when permissionsKey changes
  const sharedProjectIds = useMemo(() => {
    if (!permissions) return [];
    return [...new Set([
      ...(permissions.projectRead || []), 
      ...(permissions.projectWrite || [])
    ])];
  }, [permissionsKey]); // Use the stable string key

  // Add a flag to prevent multiple calls
  const hasLoaded = useRef(false);

  useEffect(() => {
    if (currentUser?.uid && !permissionsLoading && !isProjectsLoading && !projectsLoaded && !hasLoaded.current) {
      hasLoaded.current = true;
      getProjectSchemas(currentUser.uid, sharedProjectIds);
    }
  }, [currentUser?.uid, permissionsLoading, isProjectsLoading, projectsLoaded, permissionsKey]);

  return {
    projects,
    ownedProjects,
    sharedProjects,
    isProjectsLoading: isProjectsLoading || permissionsLoading,
    projectsLoaded: projectsLoaded && !permissionsLoading,
    permissions,
    currentUser
  };
};